/**
 * trustBadges.js — DNA-driven trust badge resolver untuk shared storefront runtime.
 *
 * Precedence (mengikuti P0 hotfix Template DNA Runtime Binding v1):
 *   1) tenant/template DNA configured `trust_badges` (jika non-empty & valid)
 *   2) generic neutral fallback (business-agnostic, aman untuk semua kategori)
 *
 * Skema entry DNA (dua format didukung agar backward compatible dan mudah diedit):
 *   - String            : "Kualitas terjamin"            → icon default "shield"
 *   - Object            : { icon: "truck"|"shield"|"sparkles", text: "..." }
 *
 * Aturan normalisasi:
 *   - Trim whitespace, drop entry kosong.
 *   - Icon key tidak dikenal → jatuh ke "shield".
 *   - Maksimum 2 badge dirender (menjaga layout premium 2-kolom di ProductDetail).
 *   - Jika DNA hanya berisi 1 badge valid, slot ke-2 diisi generic fallback yang
 *     tidak duplikat dengan copy tenant.
 *
 * PENTING: Icon TIDAK di-import di module ini agar helper murni & mudah di-unit-test
 * tanpa React/DOM. Component konsumen memanggil `iconComponentFor(key)` yang
 * mengembalikan komponen lucide-react.
 */
import { Truck, ShieldCheck, Sparkles, Lock, Headset, BadgeCheck, RefreshCw } from "lucide-react";

const ICON_COMPONENTS = {
  truck: Truck,
  shield: ShieldCheck,
  sparkles: Sparkles,
  // Additive (Global Commerce Premium trust strip) — key lama tidak berubah.
  lock: Lock,
  headset: Headset,
  "badge-check": BadgeCheck,
  refresh: RefreshCw,
};

const KNOWN_ICON_KEYS = Object.keys(ICON_COMPONENTS);

/**
 * Generic neutral fallback — business-agnostic, aman untuk Fashion/Grocery/Electronics/dll.
 * TIDAK boleh mengandung kata bisnis spesifik (telur, segar, butir, dst).
 */
export const GENERIC_TRUST_BADGES = Object.freeze([
  Object.freeze({ icon: "truck", text: "Pengiriman cepat seluruh Indonesia" }),
  Object.freeze({ icon: "shield", text: "Kualitas produk terjamin" }),
]);

const MAX_BADGES = 2;

function normalizeIconKey(key) {
  const k = String(key || "").toLowerCase().trim();
  return KNOWN_ICON_KEYS.includes(k) ? k : "shield";
}

function normalizeEntry(entry) {
  if (typeof entry === "string") {
    const text = entry.trim();
    return text ? { icon: "shield", text } : null;
  }
  if (entry && typeof entry === "object") {
    const text = String(entry.text || "").trim();
    if (!text) return null;
    return { icon: normalizeIconKey(entry.icon), text };
  }
  return null;
}

/**
 * Resolve trust badges dari DNA config.
 * @param {object|null|undefined} dna - config.dna dari StorefrontConfigContext.
 * @returns {Array<{icon: string, text: string}>} 1..2 entry (never empty).
 */
export function resolveTrustBadges(dna, { max = MAX_BADGES, pad = true } = {}) {
  const raw = dna && Array.isArray(dna.trust_badges) ? dna.trust_badges : [];
  const normalized = [];
  for (const entry of raw) {
    const n = normalizeEntry(entry);
    if (n) normalized.push(n);
    if (normalized.length >= max) break;
  }

  if (normalized.length === 0) {
    // Return a mutable copy of the frozen fallback so callers can safely iterate.
    return GENERIC_TRUST_BADGES.map((b) => ({ ...b }));
  }

  // Pad ke-2 slot dengan fallback yang bukan duplikat copy tenant.
  if (pad && normalized.length < MAX_BADGES) {
    const usedTexts = new Set(normalized.map((b) => b.text.toLowerCase()));
    for (const filler of GENERIC_TRUST_BADGES) {
      if (normalized.length >= MAX_BADGES) break;
      if (!usedTexts.has(filler.text.toLowerCase())) {
        normalized.push({ ...filler });
        usedTexts.add(filler.text.toLowerCase());
      }
    }
  }

  return normalized;
}

/**
 * @param {string} key - "truck" | "shield" | "sparkles"
 * @returns {React.ComponentType} lucide-react icon component, default ShieldCheck.
 */
export function iconComponentFor(key) {
  return ICON_COMPONENTS[normalizeIconKey(key)] || ShieldCheck;
}
